import { deepMerge } from 'js/utils/Utils'

const defaults = {
  baseWidth: {
    default: 1440,
    tablet: 1024,
  },
  $name: {
    target: '[data-img-src]',
  },
  dataName: {
    src: {
      default1x: 'imgSrc',
      default2x: 'imgSrc-2x',
      mobile1x: 'imgSrcMobile',
      mobile2x: 'imgSrcMobile-2x',
      mobile3x: 'imgSrcMobile-3x',
      tablet1x: 'imgSrcTablet',
      tablet2x: 'imgSrcTablet-2x',
    },
    glTexture: 'glImg',
  },
  outputTypeName: {
    default1x: 'default1x',
    default2x: 'default2x',
    mobile1x: 'mobile1x',
    mobile2x: 'mobile2x',
    mobile3x: 'mobile3x',
    tablet1x: 'tablet1x',
    tablet2x: 'tablet2x',
  },
  drawTypeName: {
    default: 'default',
    gl: 'gl',
  },
}

export default class BaseImagesLoader {
  constructor(options = {}) {
    this.$content = document
    this.$targetAll = null

    this.useWebgl = false
    this.isMobileLayout = false

    this.outputType = null

    this.sources = {}
    this.imageInfoList = []
    this.srcLength = 0

    deepMerge(this, defaults, options)
  }

  init() {
    this.set$()
    this.setOutputType()
    this.setImageInfoList()
  }

  set$() {
    this.$targetAll = this.$content.querySelectorAll(this.$name.target)
  }

  setOutputType() {
    const dpr = window.devicePixelRatio
    const actualWidth = window.innerWidth * dpr

    if (this.isMobileLayout) {
      if (dpr > 2) {
        this.outputType = this.outputTypeName.mobile3x
      } else if (dpr > 1) {
        this.outputType = this.outputTypeName.mobile2x
      } else {
        this.outputType = this.outputTypeName.mobile1x
      }
    } else if (!this.useWebgl) {
      if (this.baseWidth.tablet <= actualWidth) {
        this.outputType = this.outputTypeName.tablet1x
      } else {
        this.outputType = this.outputTypeName.tablet2x
      }
    } else if (this.baseWidth.default <= actualWidth) {
      this.outputType = this.outputTypeName.default2x
    } else {
      this.outputType = this.outputTypeName.default1x
    }
  }

  setImageInfoList() {
    let countAll = 0

    this.$targetAll.forEach(($el, i) => {
      this.imageInfoList[i] = {}
      this.imageInfoList[i].$target = $el

      const data = this.imageInfoList[i].$target.dataset
      let mbImage, tbImage

      switch (this.outputType) {
        case this.outputTypeName.mobile3x:
          mbImage = data[this.dataName.src.mobile3x] || data[this.dataName.src.mobile2x] || data[this.dataName.src.mobile1x]
          this.imageInfoList[i].src = mbImage || data[this.dataName.src.default1x]
          break

        case this.outputTypeName.mobile2x:
          mbImage = data[this.dataName.src.mobile2x] || data[this.dataName.src.mobile1x]
          this.imageInfoList[i].src = mbImage || data[this.dataName.src.default1x]
          break

        case this.outputTypeName.mobile1x:
          mbImage = data[this.dataName.src.mobile1x] || data[this.dataName.src.mobile2x]
          this.imageInfoList[i].src = data[this.dataName.src.mobile] || data[this.dataName.src.default1x]
          break

        case this.outputTypeName.tablet2x:
          tbImage = data[this.dataName.src.tablet2x] || data[this.dataName.src.tablet1x]
          this.imageInfoList[i].src = tbImage || data[this.dataName.src.default2x] || data[this.dataName.src.default1x]
          break

        case this.outputTypeName.tablet1x:
          tbImage = data[this.dataName.src.tablet1x] || data[this.dataName.src.tablet2x]
          this.imageInfoList[i].src = tbImage || data[this.dataName.src.default1x]
          break

        case this.outputTypeName.default2x:
          this.imageInfoList[i].src = data[this.dataName.src.default2x] || data[this.dataName.src.default1x]
          break

        default:
          this.imageInfoList[i].src = data[this.dataName.src.default1x]
          break
      }

      if (this.useWebgl && this._isDrawTypeGl(data)) {
        this.imageInfoList[i].drawType = this.drawTypeName.gl
      } else {
        this.imageInfoList[i].drawType = this.drawTypeName.default
      }

      countAll++
    })

    if (this.useWebgl) {
      Object.keys(this.sources).forEach((key) => {
        this.imageInfoList[countAll] = {}

        const sourceList = this.sources[key]

        switch (this.outputType) {
          case this.outputTypeName.mobile3x:
            this.imageInfoList[countAll].src = sourceList[this.outputTypeName.default2x] || sourceList[this.outputTypeName.default1x]
            break

          case this.outputTypeName.mobile2x:
            this.imageInfoList[countAll].src = sourceList[this.outputTypeName.default2x] || sourceList[this.outputTypeName.default1x]
            break

          case this.outputTypeName.mobile1x:
            this.imageInfoList[countAll].src = sourceList[this.outputTypeName.default1x] || sourceList[this.outputTypeName.default2x]
            break

          case this.outputTypeName.tablet2x:
            this.imageInfoList[countAll].src = sourceList[this.outputTypeName.default2x] || sourceList[this.outputTypeName.default1x]
            break

          case this.outputTypeName.tablet1x:
            this.imageInfoList[countAll].src = sourceList[this.outputTypeName.default1x] || sourceList[this.outputTypeName.default2x]
            break

          case this.outputTypeName.default2x:
            this.imageInfoList[countAll].src = sourceList[this.outputTypeName.default2x] || sourceList[this.outputTypeName.default1x]
            break

          default:
            this.imageInfoList[countAll].src = sourceList[this.outputTypeName.default1x] || sourceList[this.outputTypeName.default2x]
            break
        }

        this.imageInfoList[countAll].key = key
        this.imageInfoList[countAll].drawType = this.drawTypeName.gl

        countAll++
      })
    }
  }

  async load(imageInfo, onComplete) {
    await new Promise((resolve) => {
      const image = new Image()

      image.onload = () => {
        resolve()
        onComplete(imageInfo)
      }

      image.onerror = () => {
        resolve()
        onComplete(imageInfo)
      }

      image.src = imageInfo.src
    })
  }

  _isDrawTypeGl(data) {
    return data[this.dataName.glTexture] || data[this.dataName.glTexture] === ''
  }
}